/**
 * ExposureWindowTracker — Andes orthohantavirus 7-39 day incubation countdown.
 *
 * Surfaces discrete cohorts (e.g. "Deck crew, MV Hondius", "29 passengers
 * disembarked at Saint Helena") extracted from WHO DON narratives and news
 * articles, with a live countdown to the 39-day maximum incubation ceiling.
 *
 * Design rules followed:
 * - `.claude/rules/uncodixify-ui.md`:  no hover lift, rounded-md/lg, shadow-sm
 * - `.claude/rules/rtl-support-arabic.md`: logical margin (ms/me) only, ArrowRight mirrors
 * - `.claude/rules/accessibility-and-content-provenance.md`: WCAG 2.1 AA,
 *   touch targets ≥ 44 × 44 px via parent containers, not on the element
 */

import { useEffect, useState } from "react";

import { type TranslateFn, formatDate } from "#/lib/i18n.js";
import {
  useExposureCohorts,
  type ExposureCohort,
} from "#/hooks/useExposureCohorts.js";

type Props = {
  /** Outbreak slug forwarded from the SSR page props. */
  slug: string;
  locale: string;
  t: TranslateFn;
  /** Override for testing. */
  apiBaseUrl?: string;
};

// ---------------------------------------------------------------------------
// Countdown helpers
// ---------------------------------------------------------------------------

type CountdownParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
};

function computeCountdown(windowClosesAt: Date, now: Date): CountdownParts {
  const total = Math.max(
    0,
    Math.floor((windowClosesAt.getTime() - now.getTime()) / 1000),
  );
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { total, days, hours, minutes, seconds };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

// ---------------------------------------------------------------------------
// Live countdown hook — ticks every 60 s (no need for second-precision;
// the total is days-to-hours-scale)
// ---------------------------------------------------------------------------

function useLiveCountdown(
  windowClosesAt: Date | null,
  intervalMs = 60_000,
): CountdownParts | null {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  if (!windowClosesAt) return null;
  return computeCountdown(windowClosesAt, now);
}

// ---------------------------------------------------------------------------
// Single cohort card
// ---------------------------------------------------------------------------

function CohortCard({
  cohort,
  locale,
  t,
}: {
  cohort: ExposureCohort;
  locale: string;
  t: TranslateFn;
}) {
  const countdown = useLiveCountdown(cohort.windowClosesAt);
  const isActive = countdown !== null && countdown.total > 0;
  const hasWindow = cohort.windowClosesAt !== null;

  const hasBoarding =
    cohort.boardedAt !== null || cohort.disembarkedAt !== null;

  return (
    <article
      className="rounded-lg border bg-card text-card-foreground shadow-sm"
      aria-label={cohort.groupLabel}
      data-testid="cohort-card"
    >
      {/* Card header */}
      <div className="border-b border-border px-4 py-3 flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold leading-snug">
          {cohort.groupLabel}
        </h3>
        {hasWindow && (
          <span
            className={[
              "shrink-0 inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
              isActive
                ? "bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300"
                : "bg-green-50 text-green-900 border border-green-200 dark:bg-green-900/20 dark:text-green-300",
            ].join(" ")}
            aria-label={
              isActive
                ? t("exposure:active")
                : t("exposure:expired")
            }
            data-testid={isActive ? "badge-active" : "badge-expired"}
          >
            {isActive ? t("exposure:active") : t("exposure:expired")}
          </span>
        )}
      </div>

      {/* Card body */}
      <div className="px-4 py-3 space-y-2 text-sm">
        {/* Boarding / disembark dates */}
        {hasBoarding ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
            {cohort.boardedAt && (
              <>
                <dt>{t("exposure:boarded")}</dt>
                <dd className="font-medium text-foreground tabular-nums">
                  {formatDate(cohort.boardedAt, locale, { dateStyle: "medium" })}
                </dd>
              </>
            )}
            {cohort.disembarkedAt && (
              <>
                <dt>{t("exposure:disembarked")}</dt>
                <dd className="font-medium text-foreground tabular-nums">
                  {formatDate(cohort.disembarkedAt, locale, {
                    dateStyle: "medium",
                  })}
                </dd>
              </>
            )}
          </dl>
        ) : (
          <p className="text-muted-foreground italic text-xs">
            {t("exposure:awaitingSource")}
          </p>
        )}

        {/* Countdown */}
        {hasWindow && countdown !== null && (
          <div
            className="rounded-md bg-muted/50 px-3 py-2 text-center"
            aria-live="polite"
            aria-label={t("exposure:windowCloses")}
          >
            {isActive ? (
              <span
                className="font-mono text-lg font-semibold tracking-wide text-foreground"
                data-testid="countdown-value"
              >
                {countdown.days}d {pad(countdown.hours)}h {pad(countdown.minutes)}m
              </span>
            ) : (
              <span
                className="font-mono text-lg font-semibold tracking-wide text-muted-foreground line-through"
                data-testid="countdown-expired"
              >
                {t("exposure:expired")}
              </span>
            )}
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("exposure:windowCloses")}:{" "}
              {formatDate(cohort.windowClosesAt!, locale, {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </p>
          </div>
        )}

        {/* Headcount + attack-rate ceiling */}
        {(cohort.headcount !== null && cohort.headcount > 0) && (
          <p className="text-muted-foreground">
            {t("exposure:cohort")}:{" "}
            <span className="font-medium text-foreground">
              {cohort.headcount.toLocaleString(locale)}
            </span>
          </p>
        )}
      </div>

      {/* Card footer — source link; touch target via parent container */}
      <div className="border-t border-border px-4">
        <div className="flex items-center min-h-[44px]">
          <a
            href={cohort.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline transition-colors duration-150"
            aria-label={`${t("citations:label")}: ${cohort.sourceUrl}`}
          >
            {t("citations:label")}
            {/* Inline arrow — mirrors in RTL via scale transform */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3 w-3 rtl:scale-x-[-1]"
              aria-hidden="true"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function CohortSkeleton() {
  return (
    <div
      className="rounded-lg border bg-card shadow-sm animate-pulse" /* uncodixify-ok: CohortSkeleton loading placeholder */
      aria-hidden="true"
    >
      <div className="border-b border-border px-4 py-3">
        <div className="h-4 w-3/4 rounded bg-muted" />
      </div>
      <div className="px-4 py-3 space-y-2">
        <div className="h-3 w-1/2 rounded bg-muted" />
        <div className="h-10 w-full rounded bg-muted" />
      </div>
    </div>
  );
}

function ExposureMessage({
  message,
  t,
  tone = "muted",
}: {
  message: string;
  t: TranslateFn;
  tone?: "muted" | "error";
}) {
  return (
    <section
      id="exposure-tracker"
      aria-label={t("exposure:sectionLabel")}
      className="space-y-4"
    >
      <SectionHeading t={t} />
      <div
        className={[
          "rounded-lg border px-4 py-4 text-sm",
          tone === "error"
            ? "border-destructive/30 bg-destructive/5 text-destructive"
            : "border-border bg-card text-muted-foreground",
        ].join(" ")}
        role={tone === "error" ? "alert" : "status"}
      >
        {message}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main tracker component
// ---------------------------------------------------------------------------

export function ExposureWindowTracker({ slug, locale, t, apiBaseUrl }: Props) {
  const { data: cohorts, isLoading, isError } = useExposureCohorts(slug, {
    apiBaseUrl,
  });

  if (isLoading) {
    return (
      <section
        aria-label={t("exposure:sectionLabel")}
        aria-busy="true"
        className="space-y-4"
      >
        <SectionHeading t={t} />
        <div className="grid grid-cols-1 @sm:grid-cols-2 @lg:grid-cols-3 gap-4">
          <CohortSkeleton />
          <CohortSkeleton />
          <CohortSkeleton />
        </div>
      </section>
    );
  }

  if (isError || !cohorts) {
    return (
      <ExposureMessage
        message={t("exposure:error")}
        t={t}
        tone="error"
      />
    );
  }

  if (cohorts.length === 0) {
    return <ExposureMessage message={t("exposure:empty")} t={t} />;
  }

  return (
    <section
      id="exposure-tracker"
      aria-label={t("exposure:sectionLabel")}
      className="space-y-4"
    >
      <SectionHeading t={t} />
      <div
        className="grid grid-cols-1 @sm:grid-cols-2 @lg:grid-cols-3 gap-4"
        role="list"
        aria-label={t("exposure:sectionLabel")}
      >
        {cohorts.map((cohort) => (
          <div key={cohort.id} role="listitem">
            <CohortCard cohort={cohort} locale={locale} t={t} />
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({ t }: { t: TranslateFn }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <h2 className="text-lg font-semibold text-foreground">
        {t("exposure:sectionLabel")}
      </h2>
      <span className="text-xs text-muted-foreground">
        {t("exposure:incubationNote")}
      </span>
    </div>
  );
}
